import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import SubPageHeader from '../../components/SubPageHeader';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { Users, UserPlus, Crown, User as UserIcon, Key, Trash, X, Check } from '@phosphor-icons/react';
import { formatDate } from '../../utils/dateUtils';

const AUTH_URL = process.env.REACT_APP_AUTH_URL || '';

interface UserData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'user';
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string;
  last_login: string | null;
}

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState('');
  const { token } = useAuth();

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user' as 'admin' | 'user',
    isActive: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${AUTH_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${AUTH_URL}/api/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'user',
        isActive: true
      });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;

    try {
      await axios.delete(`${AUTH_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const handleResetPassword = async (userId: number) => {
    const newPassword = prompt('Neues Passwort eingeben (min. 8 Zeichen):');
    if (!newPassword || newPassword.length < 8) {
      alert('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    try {
      await axios.post(`${AUTH_URL}/api/users/${userId}/set-password`, 
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Passwort erfolgreich zurückgesetzt');
    } catch (err) {
      setError('Fehler beim Zurücksetzen des Passworts');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <SubPageHeader title="Benutzerverwaltung" />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <SubPageHeader title="Benutzerverwaltung" />

      <Box sx={{
        maxWidth: 1400,
        margin: isMobile ? '1rem auto' : '2rem auto',
        padding: isMobile ? '0 1rem' : '0 2rem'
      }}>
        {/* Header */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'center'}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: '#003366', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={isMobile ? 28 : 32} weight="fill" />
            Benutzerverwaltung
          </Typography>
          <Button
            variant="contained"
            startIcon={<UserPlus size={20} weight="bold" />}
            onClick={() => setShowCreateModal(true)}
            fullWidth={isMobile}
            sx={{ backgroundColor: '#003366', '&:hover': { backgroundColor: '#002244' } }}
          >
            Neuer Benutzer
          </Button>
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Users Table */}
        <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#003366' }}>
                    {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 600 }}>ID</TableCell>}
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                    {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 600 }}>E-Mail</TableCell>}
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Rolle</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                    {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 600 }}>Erstellt</TableCell>}
                    {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 600 }}>Login</TableCell>}
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map(user => (
                    <TableRow key={user.id} hover>
                      {!isMobile && <TableCell>{user.id}</TableCell>}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {user.first_name} {user.last_name}
                          </Typography>
                          {isMobile && (
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {!isMobile && <TableCell>{user.email}</TableCell>}
                      <TableCell>
                        <Chip
                          icon={user.role === 'admin' ? <Crown size={14} weight="fill" /> : <UserIcon size={14} weight="fill" />}
                          label={user.role === 'admin' ? 'Admin' : 'User'}
                          size="small"
                          sx={{
                            backgroundColor: user.role === 'admin' ? '#ffeaa7' : '#dfe6e9',
                            color: user.role === 'admin' ? '#d63031' : '#2d3436',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.is_active ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                          label={user.is_active ? 'Aktiv' : 'Inaktiv'}
                          size="small"
                          sx={{
                            backgroundColor: user.is_active ? '#d4edda' : '#f8d7da',
                            color: user.is_active ? '#155724' : '#721c24',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell>{formatDate(user.created_at)}</TableCell>
                          <TableCell>
                            {user.last_login
                              ? formatDate(user.last_login)
                              : 'Nie'}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleResetPassword(user.id)}
                            title="Passwort zurücksetzen"
                            sx={{ color: '#003366' }}
                          >
                            <Key size={18} weight="bold" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Löschen"
                            sx={{ color: '#dc2626' }}
                          >
                            <Trash size={18} weight="bold" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Create User Dialog */}
      <Dialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UserPlus size={24} weight="bold" />
          Neuen Benutzer erstellen
        </DialogTitle>
        <form onSubmit={handleCreateUser}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Vorname"
                value={newUser.firstName}
                onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Nachname"
                value={newUser.lastName}
                onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                required
              />
              <TextField
                fullWidth
                type="email"
                label="E-Mail"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
              />
              <TextField
                fullWidth
                type="password"
                label="Passwort"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
                inputProps={{ minLength: 8 }}
                helperText="Mindestens 8 Zeichen"
              />
              <FormControl fullWidth>
                <InputLabel>Rolle</InputLabel>
                <Select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value as 'admin' | 'user'})}
                  label="Rolle"
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newUser.isActive}
                    onChange={(e) => setNewUser({...newUser, isActive: e.target.checked})}
                  />
                }
                label="Aktiv"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ backgroundColor: '#003366' }}
            >
              Benutzer erstellen
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default UserManagement;
